import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, CircularProgress } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { API_BASE_URL } from '../config';
import axios from 'axios';
import { checkSubscriptionAndRedirect } from '../utils/subscriptionHelpers';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { fetchReceipts } from '../utils/receiptsUtils';
import SpendingThisMonth from './dashboard/SpendingThisMonth';
import SpendingOverTime from './dashboard/SpendingOverTime';
import DiscountsThisMonth from './dashboard/DiscountsThisMonth';
import PWAInstallBanner from './shared/PWAInstallBanner';
import { useUpload } from '../contexts/UploadContext';

ChartJS.register(ArcElement, Tooltip, Legend);

function Dashboard() {
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const [receipts, setReceipts] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState();
  const [selectedYear, setSelectedYear] = useState();
  const { handleUpload, isUploading } = useUpload();

  useEffect(() => {
    checkSubscriptionAndRedirect(
      navigate,
      () => {/* Do nothing, user has subscription */},
      () => navigate('/checkout?redirect=subscriptionCheck')
    );
    fetchReceiptsList();
  }, [navigate]);

  const fetchReceiptsList = async () => {
    try {
      const token = localStorage.getItem('token');
      const receiptsData = await fetchReceipts(token);
      setReceipts(receiptsData || []);
    } catch (error) {
      console.error('Error fetching receipts:', error);
      setReceipts([]);
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return 'N/A';
    }
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = async (event) => {
    const files = event.target.files;
    if (files.length === 0) return;

    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/auth?mode=login');
      return;
    }

    await handleUpload(files, token, async () => {
      await fetchReceiptsList();
    });
  };

  const handleReceiptClick = (receiptId) => {
    navigate(`/receipts/${receiptId}`);
  };

  const handleMonthChange = (month, year) => {
    setSelectedMonth(month);
    setSelectedYear(year);
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          color: 'white',
          font: {
            size: 12
          }
        }
      },
      title: {
        display: true,
        text: 'Spending by Category This Month',
        color: 'white',
        font: {
          size: 16
        }
      },
    },
  };

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  return (
    <div className="container mx-auto px-4 py-8">
      <PWAInstallBanner />
      <h1 className="text-4xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-600">
        Dashboard
      </h1>
      
      {/* Action Cards - First Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <ActionCard 
          title="Upload Receipts" 
          description="Upload a new receipt for AI processing"
          primaryButtonText="Upload"
          primaryOnClick={handleUploadClick}
          secondaryButtonText="Go to Receipts"
          secondaryOnClick={() => navigate('/receipts')}
          buttonColor="blue"
          isLoading={isUploading}
        />
        <DashboardCard title="Discounts Earned">
          <DiscountsThisMonth
            selectedMonth={selectedMonth}
            selectedYear={selectedYear}
            receipts={receipts}
            handleMonthChange={handleMonthChange}
            formatDate={formatDate}
          />
        </DashboardCard>
        <ActionCard 
          title="Generate Shopping List" 
          description="Create a smart shopping list based on your purchase history"
          primaryButtonText="Generate"
          primaryOnClick={() => console.log('Generate shopping list')}
          comingSoon={true}
        />
      </div>

      {/* Dashboard Cards - Second Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
        <DashboardCard title="Spending This Month">
          <SpendingThisMonth 
            selectedMonth={selectedMonth}
            selectedYear={selectedYear}
            receipts={receipts}
            chartOptions={chartOptions}
            handleMonthChange={handleMonthChange}
            formatDate={formatDate}
            months={months}
          />
        </DashboardCard>
        <DashboardCard title="Spending Over Time">
          <SpendingOverTime receipts={receipts} />
        </DashboardCard>
      </div>

      {/* Receipts Detail Card */}
      {/* <div className="bg-gray-800 p-6 rounded-lg shadow-lg mb-8">
        <h2 className="text-2xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-600">
          Receipts Detail
        </h2>
        <div className="h-[600px] overflow-hidden">
          <ReceiptsDetail className="h-full" />
        </div>
      </div> */}

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        className="hidden"
        multiple
        accept="image/jpeg,image/heic,image/heif,image/png"
      />
    </div>
  );
}

function ActionCard({ title, description, primaryButtonText, primaryOnClick, secondaryButtonText, secondaryOnClick, buttonColor, isLoading, comingSoon }) {
  return (
    <div className="relative h-full overflow-hidden rounded-xl">
      <div className="bg-gray-900 p-6 rounded-xl shadow-lg flex flex-col h-full
        border border-gray-700/50 hover:border-blue-500/50
        hover:shadow-xl hover:shadow-purple-500/10 transition-all duration-300">
        {comingSoon && (
          <div className="absolute top-0 right-0 -mr-20 mt-6 transform rotate-45 bg-indigo-500 text-white text-xs font-semibold py-1 px-20 shadow-md">
            Coming Soon
          </div>
        )}
        <h2 className="text-2xl font-bold mb-2 text-white">{title}</h2>
        <p className="mb-4 text-gray-300 flex-grow">{description}</p>
        <div className="flex justify-center gap-2">
          <Button
            variant="contained"
            onClick={primaryOnClick}
            disabled={isLoading || comingSoon}
            startIcon={!isLoading && <CloudUploadIcon />}
            sx={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              '&:hover': {
                background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
              },
              '&:disabled': {
                background: 'rgba(255,255,255,0.1)',
                color: 'rgba(255,255,255,0.3)',
              }
            }}
          >
            {isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              primaryButtonText
            )}
          </Button>
          {secondaryButtonText && (
            <Button
              variant="outlined"
              onClick={secondaryOnClick}
              startIcon={<ReceiptIcon />}
              sx={{
                borderColor: 'rgba(255,255,255,0.2)',
                color: 'rgba(255,255,255,0.8)',
                textTransform: 'none',
                fontWeight: 500,
                px: 2,
                '&:hover': {
                  borderColor: 'rgba(255,255,255,0.4)',
                  backgroundColor: 'rgba(255,255,255,0.05)',
                }
              }}
            >
              {secondaryButtonText}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function DashboardCard({ title, children, comingSoon, maxHeight = '500px' }) {
  return (
    <div className="relative overflow-hidden rounded-xl">
      <div className="bg-gray-900 p-6 rounded-xl shadow-lg
        border border-gray-700/50 hover:border-blue-500/50
        hover:shadow-xl hover:shadow-purple-500/10 transition-all duration-300">
        {comingSoon && (
          <div className="absolute top-0 right-0 -mr-20 mt-6 transform rotate-45 bg-indigo-500 text-white text-xs font-semibold py-1 px-20 shadow-md">
            Coming Soon
          </div>
        )}
        <h2 className="text-2xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-600">
          {title}
        </h2>
        <div className="h-[calc(100%-2rem)] overflow-auto">
          {children}
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
